import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Alert, Card, CardHeader, CardTitle, CardBody, Row, Col, Button, Form, Input } from 'reactstrap'
import { getOne, update, exportDetails } from '../../services/ApiService'
import { useAlertPlus } from '../../hooks/useAlertPlus'
import AlertModal from '../../components/AlertModal'
import Uploader from './Uploader'
import config from '../../config'
import BillDetailsTable from 'components/Bills/BillDetailsTable'

import './Bill.scss'

// Lista de estados que permiten la edición
const editableStates = [
  'Pendiente',
  'Observado'
]

function padPV(val) {
  const trimmed = val.trim()
  return trimmed ? trimmed.padStart(5, '0') : trimmed
}

function padNF(val) {
  const trimmed = val.trim()
  return trimmed ? trimmed.padStart(8, '0') : trimmed
}

function Edit() {
  const { id } = useParams()
  const [bill, setBill] = useState({})
  const [billDetails, setBillDetails] = useState({})
  const [alertShow, alertMessage, alertCallback, setAlert] = useAlertPlus('')
  const history = useHistory()
  const formElement = useRef(null)
  const [ isLoading, setLoading ] = useState(true)
  const [factuUnavailable, setFactuUnavailable] = useState(false)

  useEffect(() => {
    getOne('submission-orders', id)
    .then(result => {
      if(!result) {
        setLoading(false)
        return () => setAlert('No se pudo encontrar la facturación solicitada.', () => history.goBack())
      }

      if(result.message && result.message === '404 Not Found') {
        setLoading(false)
        return () => setAlert('No se pudo encontrar la facturación solicitada.', () => history.goBack())
      }

      const { bill, billDetails } = result
      setFactuUnavailable(result.factuUnavailable === true)

      // Si el status no permite la edición, redirigimos a la pantalla de sólo vista
      if (!editableStates.includes(bill.status)) {
        setLoading(false)
        history.replace({
          pathname: `/facturacion/${ id }`,
          state: { success: true }
        })
      }

      setBill(bill)
      setBillDetails(billDetails)
      setLoading(false)
    })
    .catch(error => {
      console.error(error)
      setLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exportBillDetails = async () => {
    try {
      setLoading(true)

      // Enviamos la petición
      const res = await exportDetails('submission-orders', id, billDetails)

      if (res.error) {
        setAlert('Ha ocurrido un error exportar, por favor intente nuevamente.')
        setLoading(false)
        return
      }

      setLoading(false)
      return
    } catch(err) {
      console.log(err)
      setLoading(false)
    }
  }

  const sendBill = async () => {
    try {
      setLoading(true)

      // Preparamos los inputs menos los archivos
      const data = {}
      new FormData(formElement.current).forEach((value, key) => data[key] = value);

      // Combinar punto de venta y número de factura
      const pv = (data.puntoVenta || '').trim()
      const nf = (data.nroFacturaNum || '').trim()
      if (!pv || !nf) {
        setAlert('Falta ingresar el Punto de Venta y/o el N° de Factura.')
        setLoading(false)
        return
      }
      data.nroFactura = `${padPV(pv)}-${padNF(nf)}`
      delete data.puntoVenta
      delete data.nroFacturaNum

      // Preparamos los archivos
      const files = {}
      const inputs = document.querySelectorAll('input[type="file"]');
      for (let i = 0; i < inputs.length; i++) {
        if (inputs[i].hasAttribute('required') && inputs[i].value === '') {
          setAlert('Faltan subir archivos requeridos para poder enviar la solicitud.')
          setLoading(false)
          return
        }

        files[inputs[i].name] = inputs[i].files[0]
      }

      // Enviamos la petición
      const res = await update('submission-orders', id, data, files)

      if (res.error) {
        setAlert('Ha ocurrido un error al guardar la solicitud, por favor intente nuevamente.')
        setLoading(false)
        return
      }

      return (
        setLoading(false),
        history.push({
          pathname: '/facturacion',
          state: { success: true }
        })
      )
    } catch(err) {
      console.log(err)
      setLoading(false)
    }
  }

  return (
    <div className='content'>
      <Row>
        <Col md='12'>
          <Card className='card-user'>
            <CardHeader>
              <Row>
                <Col md='8'>
                    <CardTitle tag='h5'>Solicitud #{ id } - { bill ? bill.status : 'Borrador' }</CardTitle>
                </Col>
                <Col md='4' className='back-button-container'>
                  <Button className='btn-round' color='primary' onClick={() => history.goBack()}>
                    Volver
                  </Button>
                </Col>
              </Row>
              {
                bill.status === 'Observado' && Array.isArray(bill.observations) && bill.observations.length
                ? <Alert color="warning">
                    <Row className='mb-2'>
                      <Col md='12'>
                        <span className='group-name observations'>
                          Observaciones
                          {
                            bill.observations.map((obs) => (
                              <p className='mt-2' key={obs.id}>
                                <span><strong>{new Date(obs.createdAt).toLocaleString('es')}</strong> - </span>
                                { obs.text }
                              </p>
                            ))
                          }
                        </span>
                      </Col>
                    </Row>
                  </Alert>
                : null
              }
            </CardHeader>
            <CardBody>
              <Form innerRef={ formElement }>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>Obra Social</strong></Col>
                  <Col md='9' sm='12'>{ bill.ooss?.nombre } ({ bill.ooss?.cuit })</Col>
                </Row>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>Tipo de Factura</strong></Col>
                  <Col md='9' sm='12'>{ bill.billType }</Col>
                </Row>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>Concepto</strong></Col>
                  <Col md='9' sm='12'>{ bill.concept }</Col>
                </Row>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>Periodo</strong></Col>
                  <Col md='9' sm='12'>{ bill.monthPeriod }/{ bill.yearPeriod }</Col>
                </Row>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>Fecha de Solicitud</strong></Col>
                  <Col md='9' sm='12'>{ new Date(bill.createdAt).toLocaleDateString('es-AR', { dateStyle: 'short' }) }</Col>
                </Row>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>Monto</strong></Col>
                  <Col md='9' sm='12'>$ { bill.amount }</Col>
                </Row>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>Punto de Venta</strong> *</Col>
                  <Col md='2' sm='12'>
                    <Input
                      type="text"
                      name="puntoVenta"
                      required
                      maxLength={5}
                      defaultValue={bill.nroFactura?.includes('-') ? bill.nroFactura.split('-')[0] : ''}
                      onBlur={(e) => { e.target.value = padPV(e.target.value) }}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md='3' sm='12'><strong>N° de Factura</strong> *</Col>
                  <Col md='3' sm='12'>
                    <Input
                      type="text"
                      name="nroFacturaNum"
                      required
                      maxLength={8}
                      defaultValue={bill.nroFactura?.includes('-') ? bill.nroFactura.split('-')[1] : ''}
                      onBlur={(e) => { e.target.value = padNF(e.target.value) }}
                    />
                  </Col>
                </Row>
                <Row>
                  <div className='update ml-auto mr-4'>
                    <Button className='btn-round ml-4' color='success' title='Descargar Excel' onClick={ () => exportBillDetails() } disabled={ isLoading }>
                      Descargar Excel
                    </Button>
                  </div>
                </Row>
                {factuUnavailable && (
                  <Row className="mb-2">
                    <Col>
                      <Alert color="warning">
                        Los montos por afiliado no están disponibles en este momento (servicio Factu sin respuesta).
                      </Alert>
                    </Col>
                  </Row>
                )}
                <Row className="mt-4 m-2">
                  <BillDetailsTable
                    isLoading={isLoading}
                    items={billDetails}
                  />
                </Row>
                {
                  bill.lastRejectReason &&
                    <Row className="mb-2">
                      <Col md='2' sm='12'><strong>Observación</strong></Col>
                      <Col md='10' sm='12'>{ bill.lastRejectReason }</Col>
                    </Row>
                }
                <Row className="mt-4 mb-2">
                  <Col md='2' sm='12'><strong>Factura</strong> *</Col>
                  <Col md='10' sm='12'>
                    <Uploader
                      name='factura'
                      attach={ bill.attachs ? bill.attachs[bill.attachs?.length - 1] : null }
                      required />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={{ offset: 2, size: 10 }}>
                      <label>Tamaño máximo por archivo: <strong>3 MB</strong></label>
                  </Col>
                </Row>
                <Row>
                  <div className='update ml-auto mr-auto'>
                    <Button className='btn-round ml-4' color='success' title='Enviar factura' onClick={ () => sendBill() } disabled={ isLoading }>
                      Enviar
                    </Button>
                  </div>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <AlertModal isOpen={alertShow} message={alertMessage} onClose={alertCallback} />
    </div>
  )
}

export default Edit
